import { Model, ValidationResult } from '../engine.js';
import { pathToRoot, PropertyValue, ROOT_PROPERTY } from './PropertyPathWalker.js';
import { Schema } from '../schema.js';
import { BigNumber, EnumType, MLString, ObjectType, PrimitiveType, Property, SystemType, TypeKind, TypeReference } from '../types.js';
import { ConstraintInstance, isPropertyRequired } from '../constraint.js';
import { ModelSystemProperties } from './ModelSystemProperties.js';
import { OneLevelListWalker } from './ModelWalker.js';
import { optionIdSymbol, unAvailabilitySymbol } from '../engine-impl.js';
import { Option, OptionGroup, SourcedValue } from '../option.js';

const propertyValueHasProperty = (value: any, name: string)=> {
  if (value instanceof Option) {
    return propertyValueHasProperty(value.model, name);
  } else   if (value instanceof SourcedValue) {
    return propertyValueHasProperty(value.value, name);
  } else {
    return value.hasOwnProperty(name);
  }
};
export class ModelChecker {
  public static skipModelCheck = false;

  checkModel(schema: Schema, model: Model, typeRef: TypeReference): ValidationResult {
    return this.checkProperty(schema, { value: model, property: new Property(ROOT_PROPERTY, typeRef) });
  }

  checkProperty(schema: Schema, propertyValue: PropertyValue): ValidationResult {
    if (ModelChecker.skipModelCheck) {
      return ValidationResult.Valid();
    }

    //TODO: REmove
    if (propertyValue.parent && propertyValue.parent!.value && propertyValue.parent!.value[unAvailabilitySymbol] && Object.keys(propertyValue.parent!.value[unAvailabilitySymbol]).includes(propertyValue.property.name)) {
      if (propertyValue.value) {
        throw new Error(`Unavailable property ${propertyValue.property.name} contains value '${propertyValue.value}'`)
      }
      return ValidationResult.Valid();
    }
    const type = schema.getType(propertyValue.property.type);
    const constraints: ConstraintInstance[] = propertyValue.property.opts.constraints || [];
    for (let constraint of constraints) {
      let c = schema.getConstraint(constraint.name, constraint.kwargs);
      if (!c) {
        throw new Error(`Unknown constraint:${constraint.name}`)
      }
      if (!c.valid(propertyValue.value)) {
        return ValidationResult.Error(`Constraint:${constraint.name} violation for property:${pathToRoot(propertyValue)} of type: ${propertyValue.property.type} value:${propertyValue.value}`);
      }
    }

    if (propertyValue.value) {
      if (type.kind == TypeKind.Primitive) {
        const validationResult = ModelChecker.validatePrimitiveType(type as PrimitiveType, propertyValue.value);

        if (validationResult.isInvalid) {
          return ValidationResult.Error(`${validationResult.errorMessage} in property: ${pathToRoot(propertyValue)}`);
        }
      } else if (type.kind == TypeKind.Enum) {
        const enumType = type as EnumType;
        if (!(enumType.values as any[]).includes(propertyValue.value)) {
          return ValidationResult.Error(`Value:${JSON.stringify(propertyValue.value)} of property:${pathToRoot(propertyValue)} should be in list of ${JSON.stringify(enumType.values)}`);
        }
      } else if (type.kind == TypeKind.List) {
        if (!(Array.isArray(propertyValue.value))) {
          return ValidationResult.Error(`Value:${propertyValue.value} of property:${pathToRoot(propertyValue)} should be a List of type:${propertyValue.property.type}`);
        }
        const listWalker = new OneLevelListWalker(schema, propertyValue);
        let itemPropertyValue;
        while (itemPropertyValue = listWalker.next()) {
          const validationResult = this.checkProperty(schema, itemPropertyValue);
          if (validationResult.isInvalid) {
            return validationResult;
          }
        }
        // throw new Error('List support not implemented');
      } else if (type.kind == TypeKind.Object) {
        if (!(propertyValue.value instanceof Object)) {
          return ValidationResult.Error(`Value:${propertyValue.value} of property:${pathToRoot(propertyValue)} should be an object of type:${propertyValue.property.type}`);
        }
        if (propertyValue.property.opts.needSource && ModelSystemProperties.getSource(propertyValue)) {
          const source = ModelSystemProperties.getSource(propertyValue)!.getSource() as Array<any>;
          function flatMap(o: Option | OptionGroup): Option[] {
            if(o instanceof Option) {
              return [o];
            } else {
              return (o as OptionGroup).children.reduce( (acc, x) => acc.concat(flatMap(x)), []);
            }
          }
          let src = (source as any).reduce((acc, x) => acc.concat(flatMap(x)), []);


          let inList = false;
          for (let item of src) {
            if (item._id == propertyValue.value[optionIdSymbol]) {
              inList = true;
              break;
            }
          }
          if (!inList) {
            // console.log(propertyValue)
            // console.log(source)
            return ValidationResult.Error(`Value:${JSON.stringify(propertyValue)} of property:${pathToRoot(propertyValue)} should be in list of ${JSON.stringify(source)}`);
          }
        }
        const objType = type as ObjectType;
        const fullProperties = schema.getFullObjectTypeProperties(objType)
        for (let property of fullProperties) {
          if (isPropertyRequired(property) && !propertyValueHasProperty(propertyValue.value, property.name)) {
            return ValidationResult.Error(`Object at ${pathToRoot(propertyValue)}:${JSON.stringify(propertyValue.value)} has to have property:${property.name}`);
          }

          const validationResult = this.checkProperty(schema, {
            property: property,
            value: propertyValue.value[property.name],
            parent: propertyValue
          });
          if (validationResult.isInvalid) {
            return validationResult;
          }
        }
      }
    }
    return ValidationResult.Valid();
  }

  // @todo: consider extracting validation into separate class
  static validatePrimitiveType(type: PrimitiveType, value: unknown) {
    switch (type.valueType) {
      case SystemType.Boolean:
        if (!(typeof value === 'boolean')) {
          return ValidationResult.Error(`Value:${value} should be Boolean`);
        }
        break;
      case SystemType.Number:
        if (isNaN(Number(value))) {
          return ValidationResult.Error(`Value:${value} should be Number`);
        }
        break;
      case SystemType.String:
        if (typeof value !== 'string') {
          return ValidationResult.Error(`Value:${value} should be String`);
        }
        break;
      case SystemType.BigNumber:
        if (!(value instanceof BigNumber))
          return ValidationResult.Error(`Value:${value} should be BigNumber`);
        break;
      case SystemType.MLString:
        if (!(value instanceof MLString))
          return ValidationResult.Error(`Value:${value} should be MLString`);
        break;
    }

    return ValidationResult.Valid();
  }
}
